import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Select, Spin } from 'antd';

/**
 * 远程模糊搜索下拉选择组件
 *
 * @param {function} fetchOptions - 远程搜索函数，接收keyword，返回Promise<{value, label}[]>
 * @param {number} [debounceMs=300] - 防抖延迟(ms)
 * @param {string} [placeholder] - 占位文本
 * @param {object} [props] - 透传给Select的其他属性
 */
const SearchSelect = ({ fetchOptions, debounceMs = 300, ...props }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchRef = useRef(0);
  const loadedRef = useRef(false);

  const doFetch = useCallback((keyword) => {
    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    setLoading(true);
    fetchOptions(keyword).then((newOptions) => {
      if (fetchId === fetchRef.current) {
        setOptions(newOptions);
        setLoading(false);
      }
    });
  }, [fetchOptions]);

  const handleSearch = useMemo(() => {
    let timer;
    return (value) => {
      clearTimeout(timer);
      timer = setTimeout(() => doFetch(value || ''), debounceMs);
    };
  }, [doFetch, debounceMs]);

  const handleDropdownVisibleChange = useCallback((open) => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      doFetch('');
    }
  }, [doFetch]);

  const handleClear = useCallback(() => { loadedRef.current = false; setOptions([]); }, []);

  // 初始加载一次，保证编辑回显时 value 能匹配到 label
  React.useEffect(() => {
    loadedRef.current = true;
    doFetch('');
  }, [doFetch]);

  return (
    <Select
      showSearch
      filterOption={false}
      onSearch={handleSearch}
      onClear={handleClear}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      notFoundContent={loading ? <Spin size="small" /> : null}
      options={options}
      allowClear
      {...props}
    />
  );
};

export default SearchSelect;
